import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, CheckCircle2, CircleDollarSign, Hourglass, MessageSquareQuote, Rocket, Star } from 'lucide-react';
import { useAdminCollection } from './hooks';
import { StatCard, BookingStatusBadge, EmptyState } from './adminUi';
import { useSiteData } from '../../context/SiteDataContext';
import { seedFirestore } from '../../lib/seed';
import { formatDate, money, todayIso } from '../../lib/format';
import type { Booking, QuoteRequest, Testimonial } from '../../lib/types';

export default function Dashboard() {
  const { usingDefaults } = useSiteData();
  const bookings = useAdminCollection<Booking>('bookings');
  const quotes = useAdminCollection<QuoteRequest>('quotes');
  const reviews = useAdminCollection<Testimonial>('testimonials');
  const [seedState, setSeedState] = useState<'idle' | 'seeding' | 'done' | 'error'>('idle');
  const [seedError, setSeedError] = useState('');

  const today = todayIso();
  const monthPrefix = today.slice(0, 7);

  const pending = bookings.items.filter((b) => b.status === 'pending');
  const upcoming = bookings.items
    .filter((b) => (b.status === 'confirmed' || b.status === 'pending') && b.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const completedThisMonth = bookings.items.filter((b) => b.status === 'completed' && b.date.startsWith(monthPrefix));
  const revenueThisMonth = completedThisMonth.reduce((s, b) => s + (b.estimatedTotal || 0), 0);
  const newQuotes = quotes.items.filter((q) => q.status === 'new');
  const pendingReviews = reviews.items.filter((r) => !r.approved);

  const seed = async () => {
    setSeedState('seeding');
    setSeedError('');
    try {
      await seedFirestore();
      setSeedState('done');
    } catch (e: any) {
      setSeedError(
        e?.code === 'permission-denied'
          ? 'Permission denied — your session token is stale. Sign out, sign back in, and try again.'
          : (e?.message ?? 'Unknown error'),
      );
      setSeedState('error');
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="font-display text-2xl font-bold text-white">Dashboard</h1>
      <p className="mt-1 text-sm text-zinc-500">What's happening at Apex Car Care.</p>

      {usingDefaults && seedState !== 'done' && (
        <div className="card mt-6 flex flex-wrap items-center justify-between gap-4 border-accent-500/40 p-5">
          <div className="flex items-start gap-3">
            <Rocket size={20} className="mt-0.5 shrink-0 text-accent-400" />
            <div>
              <p className="font-semibold text-white">The site is showing built-in starter content.</p>
              <p className="mt-1 text-sm text-zinc-400">
                Publish it to your database to make packages, pricing, services, FAQs and settings
                live and editable from this panel.
              </p>
              {seedState === 'error' && (
                <p className="mt-1 text-sm text-red-400">Publishing failed: {seedError}</p>
              )}
            </div>
          </div>
          <button className="btn-primary !py-2.5 text-sm" onClick={seed} disabled={seedState === 'seeding'}>
            {seedState === 'seeding' ? 'Publishing…' : 'Publish Starter Content'}
          </button>
        </div>
      )}
      {seedState === 'done' && (
        <div className="card mt-6 flex items-center gap-3 border-emerald-500/40 p-5 text-sm text-emerald-300">
          <CheckCircle2 size={18} /> Starter content published — everything on the site is now editable from this panel.
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Pending Requests" value={pending.length} icon={<Hourglass size={18} />} accent={pending.length > 0} />
        <StatCard label="Upcoming Bookings" value={upcoming.length} icon={<CalendarDays size={18} />} />
        <StatCard label="Revenue (This Month)" value={money(revenueThisMonth)} icon={<CircleDollarSign size={18} />} />
        <StatCard label="New Quotes / Messages" value={newQuotes.length} icon={<MessageSquareQuote size={18} />} accent={newQuotes.length > 0} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-white">Next Up</h2>
            <Link to="/admin/bookings" className="text-sm text-accent-400 hover:text-accent-300">All bookings →</Link>
          </div>
          {bookings.loading ? (
            <EmptyState text="Loading…" />
          ) : upcoming.length === 0 ? (
            <EmptyState text="No upcoming bookings yet. They'll appear here as customers book online." />
          ) : (
            <ul className="mt-4 divide-y divide-white/[0.05]">
              {upcoming.slice(0, 6).map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-white">
                      {b.customer.name} · <span className="text-zinc-400">{b.packageName}</span>
                    </p>
                    <p className="text-xs text-zinc-500">
                      {formatDate(b.date)} at {b.timeSlot} · {b.vehicle.year} {b.vehicle.make} {b.vehicle.model}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="font-display font-bold text-white">{money(b.estimatedTotal)}</span>
                    <BookingStatusBadge status={b.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-white">Needs Attention</h2>
          </div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
              <span className="flex items-center gap-2.5 text-zinc-300">
                <Hourglass size={16} className="text-amber-400" /> Booking requests to confirm
              </span>
              <Link to="/admin/bookings" className="font-bold text-white hover:text-accent-300">{pending.length} →</Link>
            </li>
            <li className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
              <span className="flex items-center gap-2.5 text-zinc-300">
                <MessageSquareQuote size={16} className="text-sky-400" /> New quotes & messages
              </span>
              <Link to="/admin/quotes" className="font-bold text-white hover:text-accent-300">{newQuotes.length} →</Link>
            </li>
            <li className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
              <span className="flex items-center gap-2.5 text-zinc-300">
                <Star size={16} className="text-violet-400" /> Reviews awaiting approval
              </span>
              <Link to="/admin/testimonials" className="font-bold text-white hover:text-accent-300">{pendingReviews.length} →</Link>
            </li>
          </ul>
          <div className="mt-5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-xs leading-relaxed text-zinc-500">
            <span className="font-semibold text-zinc-300">This month:</span> {completedThisMonth.length} completed
            detail{completedThisMonth.length === 1 ? '' : 's'} totaling {money(revenueThisMonth)} (based on booking estimates).
          </div>
        </section>
      </div>
    </div>
  );
}
